<script setup lang="ts">
import { ref, watch } from 'vue';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import { requiredRule, noAmpersandRegexRule } from '@/helpers/FormRules';
import { createGroupInHierarchy } from '@/api/GroupApi';
import type { NewGroup } from '@/models/Group';

const props = defineProps<{ isOpen: boolean; parentUuid: string | null }>();
const emit = defineEmits(['update:isOpen', 'group-created']);

const isOpen = ref(props.isOpen);
const createError = ref('');
const loadingSpinnerStore = useLoadingSpinnerStore();
const createGroupValid = ref(false);
const newGroup = ref<NewGroup>({
  name: '',
  description: '',
  parentUuid: props.parentUuid || null,
});

const cancel = () => {
  isOpen.value = false;
  emit('update:isOpen', false);
};

const addGroup = async () => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await createGroupInHierarchy(newGroup.value);
    emit('group-created');
    cancel();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    createError.value = err.message || 'Error creating group';
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

watch(
  () => props.isOpen,
  (newVal) => {
    isOpen.value = newVal;
    createError.value = '';
    newGroup.value.name = '';
    newGroup.value.description = '';
  },
);

watch(
  () => props.parentUuid,
  (newParentUuid) => {
    newGroup.value.parentUuid = newParentUuid ?? null;
  },
);
</script>

<template>
  <v-dialog v-model="isOpen" max-width="600" @click:outside="cancel">
    <v-card>
      <v-card-title>{{ $gettext('Add Group') }}</v-card-title>
      <v-card-text>
        <v-form v-model="createGroupValid">
          <v-text-field
            v-model="newGroup.name"
            :label="$gettext('Name')"
            :rules="[requiredRule, noAmpersandRegexRule]"
          ></v-text-field>
          <v-text-field
            v-model="newGroup.description"
            :label="$gettext('Description')"
          ></v-text-field>
        </v-form>
        <v-alert v-if="createError" class="mx-4" type="error" outlined>
          {{ createError }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="cancel">{{ $gettext('Close') }}</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="!createGroupValid"
          @click="addGroup"
          >{{ $gettext('Add') }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
